import { useState, useRef, useMemo, useCallback } from 'react';
import { Button, cssX, Dropdown, useOutsideClick } from "@datassential/platform-ui-lib";
import useExport, { ExportRequestData, ExportChartRequestData } from '@apiHooks/useExport';
import ExportMenuItem, { ExportMenuOption } from './ExportMenuItem';

interface FooterMenuDropdownProps {
    exportData: ExportRequestData;
    isDisabled?: boolean;
}

const EXPORT_DROPDOWN_ID = "byo-export-dropdown";

const BYO_EXPORT_CONFIG = {
    exportLongTermReport: {
        exportEndpoint: 'export long term report',
        downloadEndpoint: 'export long term report',
    },
    exportTrendDetails: {
        exportEndpoint: 'export trend details',
        downloadEndpoint: 'export trend details',
    },
    exportMenuMatrix: {
        exportEndpoint: 'export menu matrix',
        downloadEndpoint: 'export menu matrix',
    },
    exportInstantChartReport: {
        exportEndpoint: 'export BYO instant chart',
        downloadEndpoint: 'export BYO instant chart with flavor',
    },
};

const FooterMenuDropdown = ({
    exportData,
    isDisabled,
}: FooterMenuDropdownProps) => {
    const exportLongTermReport = useExport<ExportRequestData>(BYO_EXPORT_CONFIG.exportLongTermReport);
    const exportTrendDetails = useExport<ExportRequestData>(BYO_EXPORT_CONFIG.exportTrendDetails);
    const exportMenuMatrix = useExport<ExportRequestData>(BYO_EXPORT_CONFIG.exportMenuMatrix);
    const exportInstantChartReport = useExport<ExportChartRequestData>(BYO_EXPORT_CONFIG.exportInstantChartReport);
    
    const downloadButtonRef = useRef<HTMLDivElement>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const handleToggle = useCallback(() => {
        setIsDropdownOpen(prev => !prev);
    }, []);

    const handleClose = useCallback(() => {
        setIsDropdownOpen(false);
    }, []);

    useOutsideClick({
        ref: downloadButtonRef,
        callback: handleClose,
        ignoreNodesIds: [EXPORT_DROPDOWN_ID],
    });

    const exportMenuOptions = useMemo<ExportMenuOption[]>(() => [
        {
            title: 'Long Term Trend',
            fileFormat: 'xls',
            onClick: () => exportLongTermReport(exportData),
        },
        {
            title: 'Trend Details',
            fileFormat: 'xls',
            onClick: () => exportTrendDetails(exportData),
        },
        {
            title: 'Menu Gap Report',
            fileFormat: 'xls',
            onClick: () => exportMenuMatrix(exportData),
        },
        {
            title: 'Instant Chart Report',
            fileFormat: 'ppt',
            onClick: () => exportInstantChartReport({
                ...exportData?.customerFilter?.mainFilter,
                instantChartId: 0,
            }),
        },
    ], [
        exportData,
        exportLongTermReport,
        exportTrendDetails,
        exportMenuMatrix,
        exportInstantChartReport,
    ]);

    return (
        <>
            <div ref={downloadButtonRef}>
                <Button
                    data-tooltip-id={EXPORT_DROPDOWN_ID}
                    onClick={handleToggle}
                    isDisabled={isDisabled}
                >
                    Download
                </Button>
            </div>

            <Dropdown
                id={EXPORT_DROPDOWN_ID}
                className={cssX("ExportDropdown")}
                isOpened={isDropdownOpen}
                place="top-start"
                columns={[{
                    id: 'export-options',
                    items: [],
                    content: exportMenuOptions.map(option => (
                        <ExportMenuItem
                            key={option.title}
                            option={option}
                            onSelect={handleClose}
                        />
                    )),
                }]}
            />
        </>
    );
};

export default FooterMenuDropdown;